import { answersOfLength } from './answersAll';
import { DEFAULT_LANGUAGE, type Language } from './languages';

/**
 * Daily rollover epoch: 2026-08-04, local midnight (spec §3.3; launch day
 * 2026-08-04 = day 0).
 *
 * Exported as a `Date` for convenience (e.g. `.getFullYear()`), but `Date`
 * objects are mutable and Object.freeze can't stop `.setFullYear()` etc.
 * from working. Derivation below reads a private millisecond snapshot
 * instead, so external mutation of this export cannot change any answer.
 */
export const DAILY_EPOCH = new Date(2026, 7, 4);
const EPOCH_MS = DAILY_EPOCH.getTime();

const MS_PER_DAY = 86_400_000;

function localMidnight(d: Date): number {
  return new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();
}

function localMidnightMs(ms: number): number {
  return localMidnight(new Date(ms));
}

/** Whole days since the epoch in local time. Math.round absorbs DST-shifted day lengths. */
export function dayNumberFor(date: Date): number {
  return Math.round((localMidnight(date) - localMidnightMs(EPOCH_MS)) / MS_PER_DAY);
}

/**
 * Answer length by local weekday (spec §10.2): Mon 4 · Tue–Thu 5 ·
 * Fri–Sat 6 · Sun 7.
 *
 * **Indexed by `Date#getDay()`, where Sunday is 0** — which is why the
 * array reads 7 first even though the spec table starts at Monday. Local
 * weekday, matching the local-midnight rollover of `dayNumberFor`.
 */
export const LADDER: readonly number[] = [7, 4, 5, 5, 5, 6, 6];

/** Today's ladder length. Total over every Date — getDay() is 0–6. */
export function dailyLength(date: Date): number {
  return LADDER[date.getDay()]!;
}

/**
 * Deterministic daily answer; positive modulo so pre-epoch dates still work.
 *
 * The length comes from the weekday and the index is the same day-number
 * modulo it has always been. For an English 5-letter day
 * `answersOfLength(5)` *is* the original `ANSWERS` array, so at the default
 * language this is arithmetically the pre-ladder function — every day
 * already played, shared or pinned resolves to the same word (see the
 * byte-stability tests in daily.test.ts).
 *
 * The ladder itself is language-neutral: Monday is four letters in both
 * languages, so the two dailies are the same shape on the same day and
 * only the pool differs. Each language therefore has its own answer for a
 * given day, which is exactly why progress is stored per language
 * (spec §10.2) — one day's puzzle in Dutch is a different puzzle.
 */
export function dailyAnswer(date: Date, language: Language = DEFAULT_LANGUAGE): string {
  const pool = answersOfLength(dailyLength(date), language);
  const n = pool.length;
  const idx = ((dayNumberFor(date) % n) + n) % n;
  return pool[idx]!;
}

/** Local-time YYYY-MM-DD, used for hint/insight quota reset keys. */
export function localDateKey(date: Date): string {
  const p = (x: number) => String(x).padStart(2, '0');
  return `${date.getFullYear()}-${p(date.getMonth() + 1)}-${p(date.getDate())}`;
}
